import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n';
import { Logo } from './Logo';

const LangToggle = ({ className = '' }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center gap-1 font-mono text-xs ${className}`}>
      <button
        type="button"
        data-testid="lang-toggle-pt"
        onClick={() => setLang('pt')}
        className={`px-1.5 py-1 transition-colors duration-200 ${
          lang === 'pt' ? 'text-silver' : 'text-steel-label hover:text-steel-light'
        }`}
      >
        PT
      </button>
      <span className="text-steel-dark">/</span>
      <button
        type="button"
        data-testid="lang-toggle-en"
        onClick={() => setLang('en')}
        className={`px-1.5 py-1 transition-colors duration-200 ${
          lang === 'en' ? 'text-silver' : 'text-steel-label hover:text-steel-light'
        }`}
      >
        EN
      </button>
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const { style } = document.body;
    const previous = { position: style.position, top: style.top, width: style.width, overflow: style.overflow };
    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.width = '100%';
    style.overflow = 'hidden';
    return () => {
      style.position = previous.position;
      style.top = previous.top;
      style.width = previous.width;
      style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const links = [
    { to: '/', label: t.nav.home, id: 'home' },
    { to: '/sobre', label: t.nav.about, id: 'about' },
    { to: '/servicos', label: t.nav.services, id: 'services' },
    { to: '/processo', label: t.nav.process, id: 'process' },
    { to: '/faq', label: t.nav.faq, id: 'faq' },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? 'border-b border-graphite-200 bg-graphite-100/90 backdrop-blur-md'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <Link to="/" data-testid="nav-logo-link" aria-label="JN Automações">
          <Logo className="h-9 w-auto" testId="nav-logo" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" data-testid="nav-links">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `group relative px-3 py-1.5 text-sm transition-colors duration-200 ${
                  isActive ? 'text-steel-bright' : 'text-steel hover:text-steel-light'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-x-3 bottom-0 h-px origin-left transition-transform duration-300 ease-out motion-reduce:transition-none ${
                      isActive
                        ? 'scale-x-100 bg-silver'
                        : 'scale-x-0 bg-silver-dim group-hover:scale-x-100'
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-8 lg:flex">
          <LangToggle />
          <Link
            to="/contato"
            data-testid="nav-cta-contact"
            className="rounded-sm bg-silver px-4 py-2 text-sm font-medium text-graphite transition-colors duration-200 hover:bg-silver-glow"
          >
            {t.nav.cta}
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="p-2 text-steel-light transition-colors duration-200 hover:text-steel-bright lg:hidden"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-x-0 top-16 bottom-0 z-40 overflow-hidden bg-graphite lg:hidden"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col gap-2 px-6 py-10">
            {[...links, { to: '/contato', label: t.nav.contact, id: 'contact' }].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                data-testid={`mobile-nav-link-${l.id}`}
                className={({ isActive }) =>
                  `border-b border-graphite-200 py-4 ${
                    isActive ? 'text-silver' : 'text-steel-light'
                  }`
                }
              >
                <span className="text-2xl font-semibold tracking-tight">{l.label}</span>
              </NavLink>
            ))}
            <div className="pt-6">
              <LangToggle />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
